import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { checkIsTaskExists, createTask as createTaskApi, editTask, removeTasks } from '@api/portalTasks';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';
import {
	ICheckIsTaskExistsDTO,
	ICreateTaskDTO,
	IDeleteTasksDTO,
	IEditTaskDTO,
} from '@interfaces/portal/dto/taskManagementDTO';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import {
	CHECK_TASK_EXISTS,
	DELETE_TASKS,
	GET_PORTAL_TASK,
	GET_PORTAL_TASKS,
	GET_PORTAL_TIMELINE,
	PORTAL_TASKS_CREATE,
	UPDATE_TASK,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IUseMutatePortalTasksReturned {
	createTask: (data: ICreateTaskDTO) => Promise<void>;
	isCreatingTask: boolean;
	updateTask: (data: IEditTaskDTO) => Promise<void>;
	isUpdatingTask: boolean;
	handleDeleteTasks: (data: IGroupOperationWithActionDTO) => void;
	handleDeleteTask: (id: string) => void;
	isLoadingDelete: boolean;
	isLoadingCheckTaskExists: boolean;
	handleCheckTaskExists: (data: ICheckIsTaskExistsDTO) => void;
}

interface IUseMutatePortalTasks {
	projectId?: string | null;
	searchValue?: string;
	status?: string | null;
	priority?: string | null;
	milestoneId?: string | null;
	assignees?: string[] | null;
	closeDeleteConfirm?: () => void;
}

export function useMutatePortalTasks({
	assignees,
	priority,
	projectId,
	status,
	milestoneId,
	searchValue,
	closeDeleteConfirm,
}: IUseMutatePortalTasks): IUseMutatePortalTasksReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: createTask,
		isLoading: isCreatingTask,
	} = useMutation<ICreateTaskDTO>({
		mutationFn: createTaskApi,
		mutationKey: [PORTAL_TASKS_CREATE],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createTaskSuccess,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_TASKS]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: updateTask,
		isLoading: isUpdatingTask,
	} = useMutation<IEditTaskDTO>({
		mutationFn: editTask,
		mutationKey: [UPDATE_TASK],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateTaskSuccess,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_TASKS]),
				queryClient.invalidateQueries([GET_PORTAL_TASK]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutate: handleDeleteTasks,
		isLoading: isLoadingDeleteTasks,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			DELETE_TASKS,
			searchValue,
			projectId,
			status,
			assignees,
			priority,
			milestoneId,
		],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: IDeleteTasksDTO = {
				searchValue,
				projectId,
				status,
				assignees,
				priority,
				milestoneId,
			};

			return removeTasks({
				filter: actionType === EMenuActionsTypes.allFiltered
					? filter
					: projectId
						? { projectId }
						: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedTasks,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_TASKS]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteTask,
		isLoading: isLoadingDeleteTask,
	} = useMutation<string>({
		mutationKey: [DELETE_TASKS],
		mutationFn: (id: string) => removeTasks({
			ids: [id],
			isAll: false,
			filter: null,
		}),
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedTask,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_TASKS]),
				queryClient.invalidateQueries([GET_PORTAL_TIMELINE]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutateAsync: handleCheckTaskExists,
		isLoading: isLoadingCheckTaskExists,
	} = useMutation<ICheckIsTaskExistsDTO>({
		mutationKey: [CHECK_TASK_EXISTS],
		mutationFn: checkIsTaskExists,
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	return {
		createTask,
		isCreatingTask,
		updateTask,
		isUpdatingTask,
		handleDeleteTasks,
		handleDeleteTask,
		isLoadingDelete: isLoadingDeleteTasks || isLoadingDeleteTask,
		handleCheckTaskExists,
		isLoadingCheckTaskExists,
	};
}
